/**
 * Authentication Callback Handler Component
 *
 * Handles the OAuth callback from Builder ID after user authentication.
 * This component:
 * 1. Checks for OAuth error parameters (e.g. access_denied when user not in app)
 * 2. Parses authcode and state from URL query parameters
 * 3. Validates state parameter against stored value (CSRF protection)
 * 4. Calls backend to finish authentication and receive SessionToken
 * 5. Redirects to main app after successful authentication
 * 6. On error, redirects to the landing page with a fixed error category
 *
 * Requirements:
 * - 1.3: Parse authcode and state from Builder ID callback
 * - 1.4: Call finishLogin with authcode to exchange for SessionToken
 * - Redirect to main app after successful authentication
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Spinner } from '@amzn/awsui-components-console';
import { authService } from '../services/authService';
import { useAuth } from '../hooks/useAuth';
import { PortalError } from '../services/customerError';

/**
 * AuthCallback Component
 *
 * Processes the OAuth callback from Builder ID and completes authentication.
 * On error, redirects to the landing page with a fixed category so the user
 * sees approved guidance within the normal app layout.
 */
export function AuthCallback() {
  const navigate = useNavigate();
  const { refreshSession, logout } = useAuth();
  // Guard against double-fire from React StrictMode or dependency changes.
  // ISAC auth codes are single-use, so we must only call finish-federation once.
  const calledRef = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      if (calledRef.current) return;
      calledRef.current = true;
      try {
        // Parse URL query parameters
        const urlParams = new URLSearchParams(window.location.search);

        // Check for OAuth error response first — the IdP redirects here with
        // ?error=...&error_description=... when the user is denied (e.g. not
        // assigned to the Identity Center application).
        const oauthError = urlParams.get('error');
        if (oauthError) {
          if (oauthError === 'access_denied') {
            throw new PortalError('applicationAccessDenied');
          }
          throw new PortalError('authenticationFailed');
        }

        const authcode = urlParams.get('code');
        const state = urlParams.get('state');

        // Validate required parameters
        if (!authcode) {
          throw new PortalError('authenticationFailed');
        }

        if (!state) {
          throw new PortalError('authenticationFailed');
        }

        // Note: ISAC encodes/wraps the original state parameter, so the value
        // returned in the callback URL won't match the raw UUID we stored.
        // The backend's finish-federation endpoint validates the state with ISAC,
        // which is the authoritative CSRF check. We just verify a state exists.
        sessionStorage.removeItem('auth_state');

        // Strip trailing '?' that ISAC sometimes appends to the state parameter.
        // This is a known issue where the OAuth redirect URL's query string parsing
        // leaves a trailing '?' on the last parameter value.
        const cleanState = state.replace(/\?+$/, '');

        // Call backend to finish authentication
        // Backend sets SessionToken as httpOnly cookie automatically
        await authService.finishLogin(authcode, cleanState);

        // Refresh session to get AWS credentials
        await refreshSession();

        // Redirect to main app
        navigate('/', { replace: true });
      } catch (err) {
        console.error('Authentication callback error:', err);
        // Clear any partial auth state, then redirect to landing page with error.
        // This ensures the user sees the error within the normal app layout.
        try {
          await logout();
        } catch (logoutErr) {
          console.error('Failed to clear auth state during error handling:', logoutErr);
        } finally {
          const authError =
            err instanceof PortalError && err.kind === 'applicationAccessDenied'
              ? 'access_denied'
              : 'authentication_failed';
          navigate(`/?auth_error=${authError}`, { replace: true });
        }
      }
    };

    handleCallback();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Empty deps: runs once on mount. calledRef guards against StrictMode double-fire.

  // Only show a loading spinner — errors redirect to the landing page
  return (
    <Box textAlign="center" padding={{ vertical: 'xxxl' }}>
      <Spinner size="large" />
      <Box variant="p" padding={{ top: 's' }}>
        Completing authentication...
      </Box>
    </Box>
  );
}
